import { useEffect, useRef, useState, type Ref } from "react";
import {
  formatDuration,
  joinDuration,
  splitDuration,
  type DurationParts,
} from "@supermanus/document-model";

export interface DurationFieldProps {
  valueSeconds: number;
  onChange: (seconds: number) => void;
  readOnly?: boolean;
  /** Show an hours field (default true). When false, minutes can exceed 59. */
  showHours?: boolean;
  /** Show a frames field (default false). */
  showFrames?: boolean;
  fps?: number;
  className?: string;
  ariaLabel?: string;
}

function clamp(n: number, max?: number): number {
  if (!Number.isFinite(n) || n < 0) return 0;
  const v = Math.floor(n);
  return max === undefined ? v : Math.min(max, v);
}

function parseDigitField(raw: string, max?: number): number {
  const digits = raw.replace(/\D/g, "");
  if (digits === "") return 0;
  return clamp(Number(digits), max);
}

function DurationPartInput({
  value,
  pad,
  max,
  ariaLabel,
  title,
  inputRef,
  maxLength,
  onChange,
}: {
  value: number;
  pad: boolean;
  max?: number;
  ariaLabel: string;
  title?: string;
  inputRef?: Ref<HTMLInputElement>;
  maxLength?: number;
  onChange: (n: number) => void;
}) {
  const limit = maxLength ?? (pad ? 2 : 4);
  const padded = pad ? String(value).padStart(2, "0") : String(value);
  const [draft, setDraft] = useState<string | null>(null);
  const shown = draft ?? padded;

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      spellCheck={false}
      maxLength={limit}
      value={shown}
      aria-label={ariaLabel}
      title={title}
      onFocus={(e) => e.currentTarget.select()}
      onChange={(e) => {
        const clipped = e.target.value.replace(/\D/g, "").slice(0, limit);
        setDraft(clipped);
        onChange(parseDigitField(clipped, max));
      }}
      onBlur={() => setDraft(null)}
    />
  );
}

function toUiParts(
  valueSeconds: number,
  fps: number,
  showHours: boolean,
): DurationParts {
  const p = splitDuration(valueSeconds, fps);
  if (!showHours) {
    return {
      hours: 0,
      minutes: p.hours * 60 + p.minutes,
      seconds: p.seconds,
      frames: p.frames,
    };
  }
  return p;
}

function fromUiParts(
  parts: DurationParts,
  fps: number,
  showHours: boolean,
  showFrames: boolean,
): number {
  if (!showHours) {
    const totalMin = clamp(parts.minutes);
    return joinDuration(
      {
        hours: Math.floor(totalMin / 60),
        minutes: totalMin % 60,
        seconds: clamp(parts.seconds, 59),
        frames: clamp(parts.frames, fps - 1),
      },
      fps,
      showFrames,
    );
  }
  return joinDuration(
    {
      hours: clamp(parts.hours),
      minutes: clamp(parts.minutes, 59),
      seconds: clamp(parts.seconds, 59),
      frames: clamp(parts.frames, fps - 1),
    },
    fps,
    showFrames,
  );
}

/** Click-to-edit duration (t:m:s[:f]). Enter/blur saves; Escape cancels. */
export function DurationField({
  valueSeconds,
  onChange,
  readOnly,
  showHours = true,
  showFrames = false,
  fps = 25,
  className,
  ariaLabel = "Varighed",
}: DurationFieldProps) {
  const rate = Math.max(1, Math.floor(fps) || 25);
  const [editing, setEditing] = useState(false);
  const [parts, setParts] = useState<DurationParts>(() =>
    toUiParts(valueSeconds, rate, showHours),
  );
  const rootRef = useRef<HTMLDivElement>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editing) setParts(toUiParts(valueSeconds, rate, showHours));
  }, [valueSeconds, rate, showHours, showFrames, editing]);

  useEffect(() => {
    if (editing) firstInputRef.current?.focus();
  }, [editing]);

  function displayText(): string {
    return formatDuration(valueSeconds, {
      showHours: showHours ? true : false,
      showFrames,
      fps: rate,
    });
  }

  function commit(next: DurationParts = parts) {
    const seconds = fromUiParts(next, rate, showHours, showFrames);
    setEditing(false);
    setParts(toUiParts(seconds, rate, showHours));
    if (Math.abs(seconds - valueSeconds) > 1e-6) onChange(seconds);
  }

  function cancel() {
    setParts(toUiParts(valueSeconds, rate, showHours));
    setEditing(false);
  }

  if (readOnly) {
    return (
      <span className={`sm-duration-field is-readonly ${className ?? ""}`}>
        {displayText()}
      </span>
    );
  }

  if (!editing) {
    return (
      <button
        type="button"
        className={`sm-duration-field sm-duration-display sm-inline-editable ${className ?? ""}`}
        title="Klik for at redigere varighed"
        aria-label={`${ariaLabel}: ${displayText()}`}
        onClick={(e) => {
          e.stopPropagation();
          setParts(toUiParts(valueSeconds, rate, showHours));
          setEditing(true);
        }}
      >
        {displayText()}
      </button>
    );
  }

  return (
    <div
      ref={rootRef}
      className={`sm-duration-field is-editing ${className ?? ""}`}
      role="group"
      aria-label={ariaLabel}
      onClick={(e) => e.stopPropagation()}
      onBlur={(e) => {
        const next = e.relatedTarget as Node | null;
        if (next && rootRef.current?.contains(next)) return;
        commit();
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
          cancel();
        }
        if (e.key === "Enter") {
          e.preventDefault();
          commit();
        }
      }}
    >
      {showHours ? (
        <>
          <label className="sm-duration-part">
            <span className="sm-duration-unit">t</span>
            <DurationPartInput
              inputRef={firstInputRef}
              value={parts.hours}
              pad={false}
              ariaLabel="Timer"
              onChange={(hours) => setParts((p) => ({ ...p, hours }))}
            />
          </label>
          <span className="sm-duration-sep" aria-hidden>
            :
          </span>
        </>
      ) : null}
      <label className="sm-duration-part">
        <span className="sm-duration-unit">m</span>
        <DurationPartInput
          inputRef={showHours ? undefined : firstInputRef}
          value={parts.minutes}
          pad={showHours}
          max={showHours ? 59 : undefined}
          ariaLabel="Minutter"
          onChange={(minutes) => setParts((p) => ({ ...p, minutes }))}
        />
      </label>
      <span className="sm-duration-sep" aria-hidden>
        :
      </span>
      <label className="sm-duration-part">
        <span className="sm-duration-unit">s</span>
        <DurationPartInput
          value={parts.seconds}
          pad
          max={59}
          ariaLabel="Sekunder"
          onChange={(seconds) => setParts((p) => ({ ...p, seconds }))}
        />
      </label>
      {showFrames ? (
        <>
          <span className="sm-duration-sep" aria-hidden>
            :
          </span>
          <label className="sm-duration-part">
            <span className="sm-duration-unit">f</span>
            <DurationPartInput
              value={parts.frames}
              pad
              max={rate - 1}
              ariaLabel={`Frames (0–${rate - 1})`}
              title={`${rate} fps`}
              onChange={(frames) => setParts((p) => ({ ...p, frames }))}
            />
          </label>
        </>
      ) : null}
    </div>
  );
}
